
import React from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

interface ConnectCTraderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FormValues {
  csp_account_email: string;
  csp_account_name: string;
  platformType: 'broker' | 'prop_firm';
  platform: string;
}

export function ConnectCTraderDialog({
  open,
  onOpenChange
}: ConnectCTraderDialogProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const form = useForm<FormValues>();
  const brokers = ['Coinstrat Pro', 'Fxpro', 'XM', 'IC Market'];
  const propFirms = ['FTMO', 'AI Prop']; // Changed Aifunder to AI Prop

  const onSubmit = async (values: FormValues) => {
    if (!user?.id) return;
    try {
      const { error } = await supabase.from('csp_accounts').insert({
        user_id: user.id,
        csp_account_email: values.csp_account_email,
        csp_account_name: values.csp_account_name,
        [values.platformType]: values.platform,
        status: 'active',
        total_api_connections: 0,
        total_trading_accounts: 0
      });

      if (error) throw error;

      toast({
        title: "Profile created",
        description: "Your cTrader profile has been successfully created."
      });

      queryClient.invalidateQueries({ 
        queryKey: ['ctraderAccounts'],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({ 
        queryKey: ['resourceUsage'],
        refetchType: 'active'
      });

      onOpenChange(false);
      form.reset();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create profile. Please try again.",
        variant: "destructive"
      });
    }
  };

  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create cTrader Profile</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField control={form.control} name="csp_account_email" render={({
            field
          }) => <FormItem>
                  <FormLabel>Email of Profile</FormLabel>
                  <FormControl>
                    <Input type="email" required {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
            <FormField control={form.control} name="csp_account_name" render={({
            field
          }) => <FormItem>
                  <FormLabel>Name of Profile</FormLabel>
                  <FormControl>
                    <Input required {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
            <FormField control={form.control} name="platformType" render={({
            field
          }) => <FormItem>
                  <FormLabel>Platform Type</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select platform type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="broker">Broker</SelectItem>
                      <SelectItem value="prop_firm">Prop Firm</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>} />
            <FormField control={form.control} name="platform" render={({
            field
          }) => <FormItem>
                  <FormLabel>
                    {form.watch('platformType') === 'broker' ? 'Broker' : 'Prop Firm'}
                  </FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select platform" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {form.watch('platformType') === 'broker' ? 
                        brokers.map(broker => (
                          <SelectItem 
                            key={broker} 
                            value={broker}
                            disabled={broker !== 'Coinstrat Pro'} // Only allow Coinstrat Pro to be selected
                          >
                            {broker}
                            {broker !== 'Coinstrat Pro' && <span className="text-xs ml-1 text-muted-foreground">(coming soon)</span>}
                          </SelectItem>
                        )) : 
                        propFirms.map(firm => (
                          <SelectItem 
                            key={firm} 
                            value={firm}
                            disabled={firm === 'FTMO'} // Disable FTMO option
                          >
                            {firm}
                            {firm === 'FTMO' && <span className="text-xs ml-1 text-muted-foreground">(coming soon)</span>}
                          </SelectItem>
                        ))
                      }
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>} />
            <DialogFooter>
              <Button type="submit">Create Profile</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>;
}
